import { useContext, useEffect, useRef, useState } from 'react';
import { CanvasContext } from '../upload-image/CanvasContext';
import RotateButton from 'components/buttons/RotateButton';
import AdjustSliderControl from './adjust/AdjustSliderControl';

const degToRad = (deg) => (deg * Math.PI) / 180;

const rotatePoint = (x, y, cx, cy, rad) => {
  const dx = x - cx;
  const dy = y - cy;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
};

const rotateTextItem = (item, deltaDeg) => {
  const keys = ['rotation', 'angle', 'rotate', 'rotationDeg'];
  for (const k of keys) {
    if (typeof item?.[k] === 'number')
      return { ...item, [k]: item[k] + deltaDeg };
  }
  return {
    ...item,
    rotation:
      (typeof item.rotation === 'number' ? item.rotation : 0) + deltaDeg,
  };
};

// ✅ Support either `rotation` or `rot` (in case you've renamed it elsewhere)
const rotateStickerItem = (item, deltaDeg) => {
  const keys = ['rotation', 'rot', 'angle', 'rotate', 'rotationDeg'];
  for (const k of keys) {
    if (typeof item?.[k] === 'number')
      return { ...item, [k]: item[k] + deltaDeg };
  }
  return {
    ...item,
    rotation:
      (typeof item.rotation === 'number' ? item.rotation : 0) + deltaDeg,
  };
};

export default function RotateTool({ onCancel, onApplyDone }) {
  const {
    transformMode,
    canvasRef,
    imageRef,
    setTextItems,
    setStickerItems, // ✅ NEW
    setRedrawTrigger,
    rotatePreviewDeg,
    setRotatePreviewDeg,
  } = useContext(CanvasContext);

  const baseImageRef = useRef(null);
  // Snapshot of the current *canvas pixels* when entering rotate.
  // This preserves any prior 90° rotate / flip operations from CanvasRotateTool,
  // which may not be reflected in imageRef.
  const baseCanvasRef = useRef(null);

  const baseTextRef = useRef([]);
  const baseStickerRef = useRef([]); // ✅ NEW
  const frameRef = useRef({ w: 0, h: 0 });

  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (transformMode !== 'rotate') return;

    const img = imageRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    // Lock the frame to current canvas size so it always fits like before
    frameRef.current = { w: canvas.width, h: canvas.height };

    // ✅ Snapshot current canvas bitmap (includes any prior flips/90° rotates)
    const snap = document.createElement('canvas');
    snap.width = canvas.width;
    snap.height = canvas.height;
    const sctx = snap.getContext('2d');
    if (sctx) {
      sctx.setTransform(1, 0, 0, 1, 0, 0);
      sctx.clearRect(0, 0, snap.width, snap.height);
      sctx.drawImage(canvas, 0, 0);
    }
    baseCanvasRef.current = snap;

    // Keep imageRef snapshot as fallback only
    baseImageRef.current = img;

    // Snapshot text once (we do NOT mutate text during preview now)
    setTextItems((prev) => {
      baseTextRef.current = (prev || []).map((t) => ({ ...t }));
      return prev;
    });

    // ✅ Snapshot stickers once (we do NOT mutate stickers during preview now)
    setStickerItems((prev) => {
      baseStickerRef.current = (prev || []).map((s) => ({ ...s }));
      return prev;
    });

    // Start at 0 each time you enter rotate
    setRotatePreviewDeg(0);
  }, [
    transformMode,
    imageRef,
    canvasRef,
    setTextItems,
    setStickerItems,
    setRotatePreviewDeg,
  ]);

  const applyRotation = async () => {
    const deg = rotatePreviewDeg;
    if (!deg || isApplying) {
      onApplyDone?.();
      return;
    }

    const baseCanvas = baseCanvasRef.current;
    const baseImg = baseImageRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { w: frameW, h: frameH } = frameRef.current;
    if (!frameW || !frameH) return;

    setIsApplying(true);

    try {
      const rad = degToRad(deg);

      const off = document.createElement('canvas');
      off.width = frameW;
      off.height = frameH;
      const octx = off.getContext('2d');

      octx.clearRect(0, 0, frameW, frameH);
      octx.translate(frameW / 2, frameH / 2);
      octx.rotate(rad);

      // ✅ Draw from canvas snapshot first (preserves prior flip/90° rotate)
      if (baseCanvas) {
        octx.drawImage(baseCanvas, -frameW / 2, -frameH / 2, frameW, frameH);
      } else if (baseImg) {
        octx.drawImage(baseImg, -frameW / 2, -frameH / 2, frameW, frameH);
      } else {
        return;
      }

      const blob = await new Promise((resolve) =>
        off.toBlob(resolve, 'image/png'),
      );
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const newImg = new Image();
      await new Promise((resolve) => {
        newImg.onload = resolve;
        newImg.src = url;
      });
      URL.revokeObjectURL(url);

      imageRef.current = newImg;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (canvas.width !== frameW) canvas.width = frameW;
      if (canvas.height !== frameH) canvas.height = frameH;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, frameW, frameH);
      ctx.drawImage(newImg, 0, 0);

      const cx = frameW / 2;
      const cy = frameH / 2;

      // ✅ Commit text positions + rotation
      setTextItems(
        (baseTextRef.current || []).map((t) => {
          const p = rotatePoint(t.x, t.y, cx, cy, rad);
          const moved = { ...t, x: p.x, y: p.y };

          // ✅ Mirror the rotation delta when exactly one axis is flipped
          const shouldInvert = Boolean(moved.flipX) !== Boolean(moved.flipY);
          const deltaDeg = shouldInvert ? -deg : deg;

          return rotateTextItem(moved, deltaDeg);
        }),
      );

      // ✅ Commit sticker positions + rotation
      setStickerItems(
        (baseStickerRef.current || []).map((s) => {
          const p = rotatePoint(s.x, s.y, cx, cy, rad);
          const moved = { ...s, x: p.x, y: p.y };

          // If you ever add flipX/flipY to stickers, you can mirror like text:
          // const shouldInvert = Boolean(moved.flipX) !== Boolean(moved.flipY);
          // const deltaDeg = shouldInvert ? -deg : deg;

          return rotateStickerItem(moved, deg);
        }),
      );

      setRedrawTrigger((p) => p + 1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setRotatePreviewDeg(0));
      });

      onApplyDone?.();
    } finally {
      setIsApplying(false);
    }
  };

  if (transformMode !== 'rotate') return null;

  return (
    <div className="mx-auto w-full max-w-[820px]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-center gap-3 sm:flex-1">
          <RotateButton
            isApplying={isApplying}
            setRotatePreviewDeg={setRotatePreviewDeg}
            increment={-45}
          >
            <span className="text-2xl leading-none">⟲</span> 45°
          </RotateButton>

          <div className="flex-1 min-w-0">
            <AdjustSliderControl
              id="rotate-slider"
              label="Rotate"
              value={rotatePreviewDeg}
              onChange={(v) => setRotatePreviewDeg(Number(v))}
              min={-180}
              max={180}
              step={1}
              defaultValue={0}
              formatValue={(_stored, slider) => `${slider}°`}
              variant="inline"
              containerClassName="px-1"
              inlineRowClassName="flex items-center gap-2"
              labelClassName="sr-only"
              inputClassName="range-slider w-full cursor-pointer touch-none"
              valueClassName="text-xs text-stone-600 w-4 text-right tabular-nums"
            />
          </div>

          <RotateButton
            isApplying={isApplying}
            setRotatePreviewDeg={setRotatePreviewDeg}
          >
            <span className="text-2xl leading-none">⟳</span> 45°
          </RotateButton>
        </div>

        <div className="flex gap-2 sm:gap-4 sm:justify-end mb-4 sm:mb-0">
          <button
            className="flex-1 sm:flex-none p-2 rounded-lg bg-white text-stone-800 whitespace-nowrap disabled:opacity-50"
            disabled={isApplying}
            onClick={() => {
              setRotatePreviewDeg(0);
              onCancel?.();
            }}
          >
            Cancel
          </button>

          <button
            className="flex-1 sm:flex-none p-2 rounded-lg bg-button text-white whitespace-nowrap disabled:opacity-50"
            disabled={isApplying}
            onClick={applyRotation}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
